import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { PrototypeBanner } from "@/components/PrototypeBanner";
import {
  LayoutDashboard, PlusCircle, ClipboardList, CalendarCheck, User, LifeBuoy, LogOut,
  Briefcase, Clock, Inbox, BadgeEuro, Star, Settings, Users, Stethoscope, ShieldCheck,
  ListChecks, FileText, Menu, X, Percent, ScrollText,
} from "lucide-react";

const MENUS = {
  patient: [
    { to: "/paziente", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/paziente/nuova-richiesta", label: "Nuova richiesta", icon: PlusCircle },
    { to: "/paziente/richieste", label: "Le mie richieste", icon: ClipboardList },
    { to: "/paziente/prenotazioni", label: "Le mie prenotazioni", icon: CalendarCheck },
    { to: "/paziente/profilo", label: "Profilo", icon: User },
    { to: "/paziente/assistenza", label: "Assistenza", icon: LifeBuoy },
  ],
  professional: [
    { to: "/pro", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/pro/profilo", label: "Profilo professionale", icon: Briefcase },
    { to: "/pro/disponibilita", label: "Disponibilità", icon: Clock },
    { to: "/pro/richieste", label: "Richieste ricevute", icon: Inbox },
    { to: "/pro/incarichi", label: "Incarichi confermati", icon: CalendarCheck },
    { to: "/pro/pagamenti", label: "Pagamenti", icon: BadgeEuro },
    { to: "/pro/recensioni", label: "Recensioni", icon: Star },
    { to: "/pro/impostazioni", label: "Impostazioni", icon: Settings },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/utenti", label: "Gestione utenti", icon: Users },
    { to: "/admin/professionisti", label: "Professionisti & verifica", icon: ShieldCheck },
    { to: "/admin/servizi", label: "Gestione servizi", icon: Stethoscope },
    { to: "/admin/richieste", label: "Gestione richieste", icon: ListChecks },
    { to: "/admin/prenotazioni", label: "Gestione prenotazioni", icon: CalendarCheck },
    { to: "/admin/recensioni", label: "Gestione recensioni", icon: Star },
    { to: "/admin/impostazioni", label: "Commissioni & comuni", icon: Percent },
    { to: "/admin/log", label: "Log attività", icon: ScrollText },
  ],
};

const ROLE_LABEL = { patient: "Paziente / Familiare", professional: "Professionista", admin: "Amministratore" };

export function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const inProView = user?.role === "admin" && location.pathname.startsWith("/pro");
  const viewRole = inProView ? "professional" : user?.role;
  const menu = MENUS[viewRole] || [];

  const doLogout = async () => { await logout(); navigate("/login"); };

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const SidebarInner = () => (
    <>
      <div className="px-5 py-5 border-b border-[#D2E3DB]">
        <Logo to={`/${viewRole === "admin" ? "admin" : viewRole === "professional" ? "pro" : "paziente"}`} />
        <p className="mt-2 text-xs font-semibold text-[#087A73] bg-[#E0EFEA] rounded-full px-3 py-1 inline-block">
          {ROLE_LABEL[viewRole]}
        </p>
        {user?.role === "admin" && (inProView ? (
          <button data-testid="switch-to-admin" onClick={() => { setOpen(false); navigate("/admin"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-[#102A2A] text-white hover:bg-[#0b1f1f] transition-colors">
            <ShieldCheck className="h-4 w-4" /> Torna a vista Admin
          </button>
        ) : (
          <button data-testid="switch-to-pro" onClick={() => { setOpen(false); navigate("/pro"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-[#087A73] text-white hover:bg-[#06615B] transition-colors">
            <Briefcase className="h-4 w-4" /> Passa a vista Professionista
          </button>
        ))}
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {menu.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
              data-testid={`menu-${item.to.split("/").pop() || "dashboard"}`}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors ${
                active ? "bg-[#087A73] text-white shadow-sm" : "text-[#5C6B68] hover:bg-[#EAF3EF] hover:text-[#102A2A]"
              }`}>
              <Icon className="h-5 w-5 shrink-0" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t border-[#D2E3DB]">
        <button data-testid="logout-button" onClick={doLogout}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold text-[#C0392B] hover:bg-[#F9E3E0] transition-colors">
          <LogOut className="h-5 w-5" /> Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#EAF3EF]">
      <PrototypeBanner />
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex fixed top-[36px] bottom-0 w-72 bg-white border-r border-[#D2E3DB] flex-col">
          <SidebarInner />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <aside className="relative w-72 bg-white flex flex-col h-full"><SidebarInner /></aside>
          </div>
        )}

        <div className="flex-1 lg:ml-72 min-w-0">
          <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#D2E3DB] h-16 flex items-center justify-between px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
                <Menu className="h-6 w-6 text-[#102A2A]" />
              </button>
              <h1 className="text-lg sm:text-xl font-bold text-[#102A2A] truncate">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-semibold text-[#102A2A]">{user?.first_name} {user?.last_name}</span>
                <span className="text-xs text-[#5C6B68]">{user?.email}</span>
              </div>
            </div>
          </header>
          <main className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto ec-fade-in">{children}</main>
        </div>
      </div>
    </div>
  );
}
